/* eslint-disable @typescript-eslint/no-shadow */
import { storyblokEditable } from '@storyblok/react';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import Image from 'next/image';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';

import Container from '@/components/elements/Container';
import Section from '@/components/elements/Section';
import StandardText, {
  StandardTextSize,
} from '@/components/elements/StandardText';
import TopographicalVideoMap from '@/components/elements/TopoMap/TopographicalVideoMap';
import { getStoryblokImageUrl } from '@/lib/utils';

export interface HeroBasicProps {
  children?: ReactNode;
  story: any;
  blok: any;
  pre_headline?: string;
  headline?: string;
  content?: string;
  actions: [];
  large_heading?: string;
  gapBottom?: string;
  image?: object;
  video?: object;
}

// const calculateMinHeight = (height, range) => {
//   return height + height * range;
// };

const HeroBasic: FC<HeroBasicProps> = ({
  children,
  blok,
  pre_headline = blok.hero_pre_heading,
  headline = blok.hero_heading,
  content = blok.hero_content,
  actions = blok.actions,
}) => {
  const [offsetTop, setOffsetTop] = useState(0);
  // const [minHeight, setMinHeight] = useState('auto');
  const range = 1;
  const { scrollY } = useScroll();
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      const onResize = () => {
        setOffsetTop(ref.current.offsetTop);
        // setMinHeight(calculateMinHeight(ref.current.offsetHeight, range));
      };

      onResize();
      window.addEventListener('resize', onResize);

      return () => {
        window.removeEventListener('resize', onResize);
      };
    }

    return () => {};
  }, [ref]);

  const y = useSpring(
    useTransform(
      scrollY,
      [offsetTop - 500, offsetTop + 500],
      ['0%', `${range * 100}%`]
    ),
    { damping: 50, stiffness: 100 }
  );

  return (
    <Section
      gapTop="none"
      gapBottom={blok.background?.filename ? 'lg' : 'none'}
      className={`relative flex flex-col overflow-hidden ${
        blok.header_theme === 'dark' ? 'bg-theme-black' : 'bg-white'
      } ${
        blok.background?.filename
          ? 'max-h-[1000px] sm:min-h-[800px] md:h-[calc(100vh-var(--notification-height))]'
          : null
      }`}
      {...storyblokEditable(blok)}
    >
      {children}
      <Container
        size={'widest'}
        className={`flex flex-1 items-center ${
          blok.header_theme === 'dark'
            ? 'dark bg-theme-black'
            : 'light bg-white'
        }`}
      >
        {blok.background?.filename && (
          <>
            {blok.header_theme === 'dark' ? (
              <div className="absolute left-0 top-0 z-0 size-full">
                {blok.background?.filename && (
                  <TopographicalVideoMap
                    image={getStoryblokImageUrl(
                      blok.background?.filename,
                      1440,
                      0
                    )}
                  />
                )}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="absolute inset-0 overflow-hidden"
              >
                <div className="relative left-1/2 h-full w-screen">
                  <svg
                    className="absolute left-0 top-0 h-full"
                    viewBox="0 0 2128 1103"
                    fill="none"
                  >
                    <path
                      d="M994.224 564.645C871.732 389.348 749.038 214.186 626.038 39.2605C606.92 12.0989 580.595 -0.83848 546.047 0.0420721C487.846 1.5661 429.544 0.346878 371.31 0.482348C314.023 0.58395 295.041 14.5373 278.359 70.3169C218.839 269.423 159.657 468.665 100.238 667.805C75.1647 751.864 49.8543 835.855 24.6116 919.846C15.2048 951.106 2.24502 982.061 0.180934 1014.95C-0.56349 1027.07 0.89152 1039.57 6.37319 1050.4C15.9153 1069.24 36.387 1083.32 55.4037 1091.25C76.1799 1099.89 98.9186 1102.43 121.251 1102.39C343.428 1102.09 565.571 1102.39 787.748 1102.15C838.504 1102.09 864.187 1085.86 879.583 1038.59C923.098 905.114 964.345 770.931 1007.29 637.257C1016.08 609.858 1009.42 586.422 994.19 564.611"
                      fill="transparent"
                    />
                    <path
                      d="M1686.64 1101C1576.8 1101 1467 1101.17 1357.16 1100.93C1297.04 1100.8 1274.33 1084.51 1255.89 1027.24C1215.66 902.268 1176.2 777.06 1135.94 652.09C1124.73 617.308 1129.1 587.132 1150.82 556.482C1269.9 388.398 1387.96 219.637 1505.68 50.6049C1529.26 16.7375 1557.89 -1.41536 1600.76 0.447342C1654.9 2.78419 1709.21 0.921486 1763.42 1.02309C1823.68 1.15856 1843.04 15.0103 1860.12 72.3139C1925.02 290.217 1989.72 508.221 2054.22 726.259C2075.7 798.939 2097.22 871.618 2116.98 944.771C2123.62 969.258 2127.03 995.166 2127.91 1020.57C2129.3 1060.29 2114.04 1081.63 2076.07 1092.16C2054.22 1098.22 2030.7 1100.46 2007.89 1100.63C1900.8 1101.48 1793.67 1101 1686.57 1101"
                      fill="#DF1A25"
                    />
                  </svg>
                  <svg height="0" width="0" viewBox="0 0 2128 1103">
                    <defs>
                      <clipPath id="svgPath" clipPathUnits="objectBoundingBox">
                        <path d="M0.983,0.512 C0.862,0.353,0.741,0.194,0.619,0.036 C0.6,0.011,0.574,-0.001,0.54,0 C0.482,0.001,0.425,0,0.367,0 C0.311,0.001,0.292,0.013,0.275,0.064 C0.216,0.244,0.158,0.425,0.099,0.606 C0.074,0.682,0.049,0.758,0.024,0.834 C0.015,0.863,0.002,0.891,0,0.921 C-0.001,0.932,0.001,0.943,0.006,0.953 C0.016,0.97,0.036,0.983,0.055,0.99 C0.075,0.998,0.098,1,0.12,1 C0.34,1,0.559,1,0.779,1 C0.829,1,0.855,0.985,0.87,0.942 C0.913,0.821,0.954,0.699,0.996,0.578 C1,0.553,0.998,0.532,0.983,0.512"></path>
                      </clipPath>
                    </defs>
                  </svg>
                  <div className="absolute left-0 top-0 aspect-[1000/1090.17] h-full [clip-path:url(#svgPath)]">
                    <motion.div
                      ref={ref}
                      initial={{ y: 0 }}
                      style={{ y }}
                      className="absolute inset-0"
                    >
                      <Image
                        alt={blok.background?.alt || ''}
                        src={blok.background?.filename}
                        fill
                        sizes="50vw"
                        className="object-cover"
                      />
                    </motion.div>
                    <svg
                      className="absolute right-[50%] top-[15%] w-full text-theme-white/90 md:right-[60%] md:top-1/4 md:text-theme-black/50"
                      viewBox="0 0 1000 1091"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M983.197 558.383C862.064 385.029 740.73 211.81 619.095 38.8251C600.189 11.9647 574.155 -0.82918 539.991 0.0416054C482.436 1.54873 424.78 0.343031 367.192 0.476998C310.541 0.577474 291.768 14.3761 275.271 69.537C216.411 266.435 157.886 463.467 99.1266 660.399C74.3311 743.525 49.3014 826.585 24.3386 909.644C15.0361 940.557 2.22012 971.169 0.178927 1003.69C-0.55724 1015.68 0.881633 1028.04 6.3025 1038.75C15.7388 1057.38 35.9834 1071.31 54.7892 1079.15C75.335 1087.69 97.8215 1090.2 119.907 1090.16C339.619 1089.86 559.298 1090.16 779.011 1089.93C829.204 1089.86 854.602 1073.82 869.827 1027.07C912.86 895.075 953.65 762.381 996.113 630.189C1004.81 603.094 998.222 579.918 983.164 558.349"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                </div>
              </motion.div>
            )}
          </>
        )}

        <div
          className={`relative mr-8 max-w-[74ch] sm:mr-0 ${
            blok.background?.filename ? 'py-16 md:py-36' : 'py-16 md:py-24'
          } ${
            blok.background?.filename && blok.header_theme === 'light'
              ? 'pr-16 md:max-w-[50%]'
              : null
          }`}
        >
          <StandardText
            blok={{ headline, pre_headline, content, actions }}
            size={StandardTextSize.ML}
          />
        </div>
      </Container>
      {!headline && <div className="mt-4 py-16"></div>}
    </Section>
  );
};

export default HeroBasic;
